import { cn } from '@/common/utils/cn'
import { getDateDiffFormatted } from '@/common/utils/date'
import { LucideProps, XIcon } from 'lucide-react'
import { DynamicIcon, IconName, iconNames } from 'lucide-react/dynamic'
import { useEffect, useRef, useState } from 'react'
import { ToastContentProps } from 'react-toastify'
import Button from '../Button'

const NotificationToast = ({
  data,
  closeToast,
  toastProps,
}: Partial<
  ToastContentProps<{
    title: string
    date: Date
    body?: string
    icon: IconName
    iconColor: string
    iconProps?: LucideProps
  }>
>) => {
  const [progress, setProgress] = useState(100)
  const [isHovered, setIsHovered] = useState(false)

  const startTimeRef = useRef<number | null>(null)
  const frameRef = useRef<number | null>(null)
  const pausedTimeRef = useRef<number>(0)
  const pauseStartRef = useRef<number | null>(null)

  useEffect(() => {
    if (toastProps?.autoClose) {
      const duration = typeof toastProps.autoClose === 'number' ? toastProps.autoClose : 5000

      const animate = (timestamp: number) => {
        if (!startTimeRef.current) startTimeRef.current = timestamp

        if (isHovered) {
          if (!pauseStartRef.current) {
            pauseStartRef.current = timestamp
          }
          frameRef.current = requestAnimationFrame(animate)
          return
        }

        if (pauseStartRef.current) {
          pausedTimeRef.current += timestamp - pauseStartRef.current
          pauseStartRef.current = null
        }

        const elapsed = timestamp - startTimeRef.current - pausedTimeRef.current
        const percent = Math.ceil(Math.max(100 - (elapsed / duration) * 100, 0))

        setProgress(percent)

        if (percent > 0) {
          frameRef.current = requestAnimationFrame(animate)
        } else {
          closeToast?.()
        }
      }

      frameRef.current = requestAnimationFrame(animate)

      return () => {
        if (frameRef.current) cancelAnimationFrame(frameRef.current)
      }
    }
  }, [toastProps?.autoClose, isHovered])

  return (
    <div
      className="flex flex-col gap-3 w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex gap-2">
        {data?.icon && (
          <DynamicIcon
            name={iconNames.includes(data?.icon) ? data.icon : 'message-square'}
            color={data?.iconColor}
            strokeWidth={1}
            className="size-8 shrink-0"
          />
        )}

        <div className="grow flex flex-col gap-1">
          <div className="flex flex-row items-center justify-between">
            <p
              className="text-white-800 font-bold text-md"
              style={{ fontFamily: "'Merriweather Sans', Inter, system-ui, Avenir, Helvetica, Arial, sans-serif" }}
            >
              {data?.title}
            </p>

            {data?.date && (
              <p className="text-xs font-light italic text-nowrap">Há {getDateDiffFormatted(new Date(), data.date)}</p>
            )}
          </div>

          {!!data?.body?.length && (
            <div
              className={cn('overflow-hidden transition-all duration-300 ease-in-out', {
                'max-h-96': isHovered,
                'max-h-6': !isHovered,
              })}
            >
              {Array.isArray(data.body) ? (
                Array.isArray(data.body[0]) ? (
                  <>
                    {isHovered ? (
                      data.body[0]?.map((text: string, index: number) => (
                        <p
                          key={index}
                          className="text-white-800 text-sm font-light"
                          style={{
                            fontFamily: "'Merriweather Sans', Inter, system-ui, Avenir, Helvetica, Arial, sans-serif",
                          }}
                        >
                          {text}
                        </p>
                      ))
                    ) : (
                      <p
                        className="text-white-800 text-sm font-light truncate"
                        style={{
                          fontFamily: "'Merriweather Sans', Inter, system-ui, Avenir, Helvetica, Arial, sans-serif",
                        }}
                      >
                        {data.body[0][0]}
                      </p>
                    )}
                  </>
                ) : (
                  <p
                    className="text-white-800 text-sm font-light"
                    style={{
                      fontFamily: "'Merriweather Sans', Inter, system-ui, Avenir, Helvetica, Arial, sans-serif",
                    }}
                  >
                    {data.body[0]}
                  </p>
                )
              ) : (
                <p
                  className="text-white-800 text-sm font-light"
                  style={{ fontFamily: "'Merriweather Sans', Inter, system-ui, Avenir, Helvetica, Arial, sans-serif" }}
                >
                  {data.body}
                </p>
              )}
            </div>
          )}
        </div>

        <Button
          variant="link-white"
          className="size-[24px] flex items-center justify-center"
          onClick={() => toastProps?.closeToast()}
        >
          <XIcon strokeWidth={1.5} size={16} className="text-red-500" />
        </Button>
      </div>

      {toastProps?.autoClose && (
        <div className="w-full h-[3px] bg-gray-700 rounded-xl overflow-x-hidden">
          <div className="h-[3px] bg-primary-blue rounded-xl" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  )
}

export default NotificationToast
